import { useCallback, useMemo, useState } from 'react';

import {
  EmptyState,
  InteractiveDropdownFilterInput,
  InteractiveDropdownFooter,
  InteractiveDropdownHeader,
  InteractiveDropdownSelect,
  InteractiveDropdownView,
  MaterialIcon,
} from '@components';
import { Tooltip } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { routes } from '@routes';
import { CustomAnalyticsEvents, useAnalyticsContext } from '@utils/analytics';
import { useListSavedQueries } from '@Inventory/services/savedQueryQueries';
import { WITH_SAVED_QUERY_QUERY_PARAM } from '@AccessFlows/common/constants';
import { InventoryPageQueryKeys } from '@Inventory/common/queryKeys';
import { SavedQueryAppModel } from '@api';
import PermissionButton from '@common/components/PermissionButton';
import { Permission } from '@common/utils/admin_permissions';

interface DropdownSavedQueriesViewProps {
  current: boolean;
  value?: string;
  isLoading: boolean;
  onSavedQueryChange: (value: string) => void;
  onBackClick?: () => void;
}

function useDropdownSavedQueriesView({ isLoading }: Pick<DropdownSavedQueriesViewProps, 'isLoading'>) {
  const { track } = useAnalyticsContext();
  const { savedQueries, isFetched: isSavedQueriesFetched } = useListSavedQueries();
  const [filter, setFilter] = useState('');

  const handleFilterChange = (val: string) => {
    setFilter(val);
    track(CustomAnalyticsEvents.ACCESS_SCOPE_AS_ACCESS_FLOW_TARGET_PICKED, { accessScope: val });
  };

  const savedQueryView = useCallback((e: React.MouseEvent, savedQuery: SavedQueryAppModel) => {
    e.stopPropagation();
    const query = encodeURIComponent(savedQuery.query);
    const path = `${routes.Inventory.path}?${InventoryPageQueryKeys.SavedQueryId}=${savedQuery.id}&${InventoryPageQueryKeys.Query}=${query}`;
    window.open(path, '_blank');
  }, []);

  const options = useMemo(
    () =>
      savedQueries
        .map((savedQuery) => ({
          key: savedQuery.id,
          value: savedQuery.name,
          rightIcon: (
            <Tooltip placement="top" arrow title="View access scope">
              <div>
                <MaterialIcon symbol="open_in_new" color="primary" onClick={(e) => savedQueryView(e, savedQuery)} />
              </div>
            </Tooltip>
          ),
        }))
        .filter((v) => `${v.key} ${v.value}`.toLocaleLowerCase().includes(filter.toLocaleLowerCase())),
    [savedQueries, savedQueryView, filter],
  );

  const isFetched = useMemo(() => isSavedQueriesFetched && !isLoading, [isSavedQueriesFetched, isLoading]);

  return {
    isFetched,
    options,
    filter,
    handleFilterChange,
  };
}

export function DropdownSavedQueriesView({
  current,
  value,
  isLoading,
  onSavedQueryChange,
  onBackClick,
}: DropdownSavedQueriesViewProps) {
  const { isFetched, options, filter, handleFilterChange } = useDropdownSavedQueriesView({ isLoading });

  const { id: accessFlowId } = useParams();

  const editLinkQP = useMemo(() => {
    const params = new URLSearchParams();
    params.set(WITH_SAVED_QUERY_QUERY_PARAM, accessFlowId ?? true.toString());
    return params;
  }, [accessFlowId]);

  if (!isFetched) {
    return <InteractiveDropdownView current loading />;
  }

  return (
    <InteractiveDropdownView
      current={current}
      header={<InteractiveDropdownHeader title="Select access scope" onBackClick={onBackClick} />}
      filter={<InteractiveDropdownFilterInput value={filter} onChange={handleFilterChange} />}
      footer={
        options.length > 0 && (
          <InteractiveDropdownFooter>
            <PermissionButton
              component={Link}
              to={`${routes.Inventory.path}?${editLinkQP.toString()}`}
              variant="text"
              startIcon={<MaterialIcon symbol="add" />}
              data-trigger={CustomAnalyticsEvents.ACCESS_SCOPE_CREATE_INITIATED_IN_BUILDER}
              permissions={[Permission.INVENTORY_WRITE]}
            >
              Create New Access Scope
            </PermissionButton>
          </InteractiveDropdownFooter>
        )
      }
    >
      <InteractiveDropdownSelect<string>
        options={options}
        value={value}
        onChange={onSavedQueryChange}
        selectedFirst
        emptyState={
          <EmptyState
            imgSrc="/static/EmptyStateImages/not-found-illustration.svg"
            title="No access scopes created yet"
            action={
              <PermissionButton
                component={Link}
                to={`${routes.Inventory.path}?${editLinkQP.toString()}`}
                variant="contained"
                permissions={[Permission.INVENTORY_WRITE]}
              >
                Create New Access Scope
              </PermissionButton>
            }
          />
        }
      />
    </InteractiveDropdownView>
  );
}
